'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IconButton } from '@kraidle/ui/components/icon-button';
import { Modal } from '../Modal';

/** kraidle's hero + capability-detail media render as plain <img> inside these
 * slots (see @kraidle/ui asset-visual / hero / horizontal-media-card /
 * horizontal-stacked-media-card — the last is solution-page media). Resource
 * thumbnails and the SVG placeholder fallback (a role="img" <div>, no <img>) are
 * intentionally excluded. */
const MEDIA_SLOTS = [
  '[data-slot="hero-media"]',
  '[data-slot="horizontal-media-card-media"]',
  '[data-slot="horizontal-stacked-media-card-media"]',
];
// Build a comma list by appending the suffix to EACH slot — not `slots + suffix`,
// which would only suffix the last slot and leave the first matching bare slots.
const MEDIA_IMG_SELECTOR = MEDIA_SLOTS.map(s => `${s} img`).join(', ');

/** Some assets are authored as HTML documents (`type: screenshot`, `*.embed.html`)
 * rather than raster/vector files. kraidle's AssetVisual renders every asset as
 * <img src>, so an HTML doc loads into an <img> → broken image. These embeds are
 * self-contained, static, fixed-size docs we own (no scripts, no external loads).
 * We swap each into a sandboxed, same-aspect <iframe> in place. */
const EMBED_IMG_SELECTOR = MEDIA_SLOTS.map(s => `${s} img[src$=".embed.html"]`).join(', ');

// The embed docs lay out at a fixed 1280x720 (16:9) viewport with overflow hidden;
// an iframe can't reflow that, so we render it at native size and scale-to-fit the
// (also 16:9) media slot. 1280 is the design width every embed is authored against.
const EMBED_NATIVE_WIDTH = 1280;
const EMBED_NATIVE_HEIGHT = 720;

// The one scale formula that fits the fixed-layout (EMBED_NATIVE_WIDTH-wide)
// embed doc into a box of `boxWidth` px. Every place that scales an embed iframe
// (inline seed, inline ResizeObserver, enlarged modal) uses this so the inline
// and enlarged renders can never silently diverge.
const embedScale = (boxWidth: number) => `scale(${boxWidth / EMBED_NATIVE_WIDTH})`;

// `displaySrc` is the already-loaded inline variant (shown instantly, no
// broken-image flash); `fullSrc` is the full-res original we upgrade to once it
// has decoded. They're equal for plain-<img> media (svg) with no full-res twin.
type LightboxImage = { displaySrc: string; fullSrc: string; alt: string };

// An HTML-embed asset opened in the lightbox: the same self-contained doc shown
// inline, re-rendered enlarged. `src` is the embed's URL, `alt` its accessible
// name. It carries no raster twin, so it takes the <iframe> render path, not the
// <img> one — hence a distinct shape from LightboxImage.
type LightboxEmbed = { src: string; alt: string };

/**
 * Website-side image lightbox for the kraidle-rendered platform and solution
 * pages. The page is a server component rendering <CapabilitiesPage/> (product/
 * [slug]) or <SolutionsPage/> (solutions/[slug]), whose media are framework-
 * agnostic plain <img> tags we don't control — so we enhance them in place: each
 * media <img> becomes a focusable, zoom-cursor "button", and clicks / Enter /
 * Space are intercepted via event delegation on the wrapper. Selecting one opens
 * an accessible <dialog> Modal (native focus trap, ESC + click-outside to close)
 * that re-displays the same src enlarged with its alt text preserved.
 *
 * It also fixes HTML-embed assets: some media are `*.embed.html` docs that
 * kraidle still renders as <img src> (→ broken image). On mount we swap each into
 * a sandboxed, scaled <iframe> in place. Its wrapper is itself a zoom trigger, so
 * an embed opens enlarged in the lightbox too — as a re-rendered <iframe> (there's
 * no raster to show), the inline iframe kept inert (pointer-events:none) so clicks
 * reach the wrapper.
 */
export function PlatformImageLightbox({ children }: { children: React.ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  // The enlarged embed's fit-to-viewport box; its fixed-layout iframe is scaled
  // to this box's measured width. A callback ref (not a plain ref) so the initial
  // scale is seeded synchronously as the box mounts — before the browser paints —
  // which avoids the native-size flash a post-paint effect would leave. The
  // resize effect below keeps it in sync while the lightbox stays open.
  const modalEmbedRef = useRef<HTMLDivElement | null>(null);
  const setModalEmbedBox = useCallback((box: HTMLDivElement | null) => {
    modalEmbedRef.current = box;
    if (!box) return;
    // Children mount before their parent's ref fires, so the iframe is already
    // in the box here; scale it now (pre-paint) to its final size.
    const frame = box.querySelector('iframe');
    if (frame) frame.style.transform = embedScale(box.clientWidth);
  }, []);
  // The element that opened the lightbox — a media <img> or an embed wrapper
  // <div>. Focus returns here on close (Modal unmounts its dialog, dropping the
  // native restore). HTMLElement covers both.
  const triggerRef = useRef<HTMLElement | null>(null);
  const [image, setImage] = useState<LightboxImage | null>(null);
  // Set when an HTML-embed asset is opened instead of a raster/svg image; the two
  // are mutually exclusive (one trigger at a time) and drive different render
  // paths in the Modal (<iframe> vs <img>).
  const [embed, setEmbed] = useState<LightboxEmbed | null>(null);
  // The src currently painted in the modal — starts as the cached inline variant
  // and upgrades to the full-res original once it has preloaded.
  const [shownSrc, setShownSrc] = useState('');

  // Upgrade the painted src to the full-res original once it has preloaded.
  // openFor seeds `shownSrc` with the already-loaded inline variant so the
  // modal's first paint is instant and flash-free; this swaps in the crisp
  // full-res after it decodes. No distinct full-res twin → nothing to do.
  useEffect(() => {
    if (!image || image.fullSrc === image.displaySrc) return;
    const pre = new window.Image();
    let cancelled = false;
    pre.src = image.fullSrc;
    // decode() before swapping so the painted <img> upgrades from an in-memory,
    // already-decoded bitmap — no blank frame while the large original decodes.
    // decode() resolves instantly if it's already cached. On reject (decode
    // error / aborted on cleanup) keep the working inline variant — no swap.
    pre.decode().then(
      () => {
        if (!cancelled) setShownSrc(image.fullSrc);
      },
      () => {},
    );
    return () => {
      cancelled = true;
      // Abort the in-flight full-res download on close / re-open; flipping
      // `cancelled` alone would let it run to completion off-screen.
      pre.src = '';
    };
  }, [image]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Keep each embed iframe scaled to its (16:9) slot: native 1280px wide,
    // scaled by slotWidth/1280 so the fixed-layout doc fits without reflow.
    // Guarded: a browser without ResizeObserver (or one where constructing it
    // throws) must not abort the effect before the lightbox listeners attach.
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(entries => {
            for (const entry of entries) {
              const frame = entry.target.querySelector<HTMLIFrameElement>('iframe');
              if (frame) frame.style.transform = embedScale(entry.contentRect.width);
            }
          });

    // Replace HTML-embed <img>s (broken — an HTML doc in an <img>) with a
    // sandboxed <iframe> that renders the doc. Runs before enhance() so these
    // never become lightbox triggers (an iframe isn't an image to zoom).
    const convertEmbeds = () => {
      root.querySelectorAll<HTMLImageElement>(EMBED_IMG_SELECTOR).forEach(img => {
        // Wrapper takes the <img>'s place as the slot's direct child, so the
        // slot's 16:9 box + rounding still apply; it clips and positions the
        // native-size iframe we scale into it.
        const embedSrc = img.currentSrc || img.src;
        const wrapper = document.createElement('div');
        wrapper.dataset.embedFrame = 'true';
        // Stash the embed's src + alt on the wrapper so the click handler can open
        // the lightbox from it without re-reading the (pointer-events:none) iframe.
        wrapper.dataset.embedSrc = embedSrc;
        wrapper.dataset.embedAlt = img.alt;
        wrapper.className = 'relative size-full cursor-zoom-in overflow-hidden';
        // The wrapper is the zoom trigger (the iframe below is inert): same a11y
        // affordances the media <img>s get in enhance(), so keyboard + SR users
        // can open the embed too.
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'button');
        // No dangling colon when alt is empty; "view larger" (not "image") since
        // the embed is an HTML doc, not a raster image.
        wrapper.setAttribute('aria-label', img.alt ? `View larger: ${img.alt}` : 'View larger');

        const frame = document.createElement('iframe');
        frame.src = embedSrc;
        frame.title = img.alt; // a11y: the alt is the embed's accessible name.
        frame.loading = 'lazy';
        frame.tabIndex = -1; // static visual — keep it out of the tab order.
        // Self-contained, same-origin, script-free docs: the tightest sandbox
        // (no allow-*) still renders their inline CSS.
        frame.setAttribute('sandbox', '');
        // Always hidden from AT: the wrapper is a role=button carrying the
        // accessible name, and ARIA forbids exposed/interactive content inside a
        // button — so the iframe must not be a reachable region within it.
        frame.setAttribute('aria-hidden', 'true');
        // pointer-events:none so clicks fall through to the wrapper trigger — a
        // sandboxed iframe would otherwise swallow them and never open the zoom.
        frame.style.cssText = `position:absolute;top:0;left:0;width:${EMBED_NATIVE_WIDTH}px;height:${EMBED_NATIVE_HEIGHT}px;border:0;transform-origin:top left;pointer-events:none;`;

        wrapper.appendChild(frame);
        img.replaceWith(wrapper);
        // Now in the DOM: set the real initial scale (ResizeObserver keeps it
        // in sync afterwards, but seeding it here avoids a first-paint jump).
        frame.style.transform = embedScale(wrapper.clientWidth);
        resizeObserver?.observe(wrapper);
      });
    };

    // Mark each media <img> as an accessible zoom trigger. CapabilitiesPage /
    // SolutionsPage render every section (and its media) up front, so this runs once on
    // mount; the MutationObserver only guards against later DOM changes.
    const enhance = () => {
      root.querySelectorAll<HTMLImageElement>(MEDIA_IMG_SELECTOR).forEach(img => {
        if (img.dataset.lightbox) return;
        img.dataset.lightbox = 'true';
        img.tabIndex = 0;
        img.setAttribute('role', 'button');
        img.setAttribute('aria-label', `View larger image: ${img.alt}`);
        img.classList.add('cursor-zoom-in');
      });
    };

    const openFor = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      // Embed wrappers first: an embed opens as a re-rendered <iframe>, not an
      // <img>, so it takes a separate path. (Its inner iframe is
      // pointer-events:none, so `target` is always the wrapper itself.)
      const wrapper = el?.closest<HTMLElement>('[data-embed-frame="true"]');
      if (wrapper) {
        triggerRef.current = wrapper;
        setImage(null);
        setEmbed({ src: wrapper.dataset.embedSrc || '', alt: wrapper.dataset.embedAlt || '' });
        return true;
      }
      const img = el?.closest<HTMLImageElement>('img[data-lightbox="true"]');
      if (!img) return false;
      triggerRef.current = img;
      setEmbed(null);
      // displaySrc = the already-loaded inline variant (paints instantly).
      // fullSrc = the full-res original (`data-lightbox-full`, set by the
      // next/image adapter) — the inline media is a small slot-sized srcset
      // variant, but the lightbox enlarges to ~88vw, so we upgrade to the
      // original to avoid a blurry upscale. Plain <img> media (svg) has no such
      // attribute, so both fall back to its own (loaded) src — no swap, no flash.
      const displaySrc = img.currentSrc || img.src;
      // Seed the painted src synchronously so the modal's first paint already
      // shows the cached inline variant (the upgrade effect runs post-paint).
      setShownSrc(displaySrc);
      setImage({ displaySrc, fullSrc: img.dataset.lightboxFull || displaySrc, alt: img.alt });
      return true;
    };

    const onClick = (e: MouseEvent) => {
      if (openFor(e.target)) e.preventDefault();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (openFor(e.target)) e.preventDefault();
    };

    // Attach the lightbox listeners FIRST and unconditionally — zoom-on-click /
    // keyboard must work even if embed conversion below ever throws. #3201
    // regressed this by running throw-prone embed code (convertEmbeds, the
    // ResizeObserver) before the listeners, so any failure there silently killed
    // the whole lightbox.
    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeyDown);

    // Convert embeds first so they're gone before enhance() scans for
    // lightbox-able <img>s, but isolate convert in try/catch so a failure can
    // never stop enhance() from marking the real media images. Both are
    // idempotent (convert leaves no matchable <img>; enhance skips already-marked
    // ones), so the MutationObserver can re-run them on any later change without
    // looping.
    const sync = () => {
      try {
        convertEmbeds();
      } catch (err) {
        console.error('PlatformImageLightbox: embed conversion failed; lightbox still active', err);
      }
      enhance();
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      resizeObserver?.disconnect();
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Keep the enlarged embed's fixed-layout (1280×720) iframe fitted to its
  // viewport-sized box as the viewport changes while the lightbox is open — the
  // same native-size + scale trick as the inline embeds, since an iframe can't
  // reflow the doc. The initial fit is seeded pre-paint in setModalEmbedBox; this
  // only tracks later resizes. Skipped where ResizeObserver is unavailable (the
  // pre-paint seed still gives a correct static fit).
  useEffect(() => {
    if (!embed || typeof ResizeObserver === 'undefined') return;
    const box = modalEmbedRef.current;
    const frame = box?.querySelector('iframe');
    if (!box || !frame) return;
    const ro = new ResizeObserver(() => {
      frame.style.transform = embedScale(box.clientWidth);
    });
    ro.observe(box);
    return () => ro.disconnect();
  }, [embed]);

  // Modal unmounts its <dialog>, so the native focus-restore is lost; return
  // focus to the trigger (media <img> or embed wrapper) that opened the lightbox
  // once it's gone (WCAG 2.4.3).
  const handleClose = () => {
    const trigger = triggerRef.current;
    setImage(null);
    setEmbed(null);
    if (trigger) requestAnimationFrame(() => trigger.focus());
  };

  // Close control reuses kraidle's IconButton (the platform design system's own
  // base UI element) so the lightbox matches the surrounding page. Rendered as
  // the Modal header so it replaces the default heroicon close.
  const closeButton = (
    <IconButton
      type="button"
      variant="secondary"
      size="md"
      aria-label="Close image"
      onClick={handleClose}
      // Subtle translucent puck (white glyph) so it reads over any image without
      // competing with it — legible on light and dark areas alike.
      className="absolute top-3 right-3 z-10 rounded-full! border-0! bg-black/40! text-white! backdrop-blur-sm transition-colors hover:bg-black/60!"
    >
      <XMarkIcon className="size-5" />
    </IconButton>
  );

  return (
    <>
      <div ref={rootRef} data-testid="platform-lightbox-root">
        {children}
      </div>
      <Modal
        isOpen={image !== null || embed !== null}
        onClose={handleClose}
        closeOnBackdropClick
        variant="dark"
        header={closeButton}
        // Strip the Modal's card chrome: the lightbox is just the image (sized to
        // fit the viewport, so any aspect ratio fits) over the dimmed backdrop.
        className="relative! flex! max-h-none! max-w-none! flex-col! items-center! border-0! bg-transparent! p-0! shadow-none!"
      >
        {(image || embed) && (
          // One figure for both media kinds — shared wrapper + caption chrome so
          // the raster and embed lightboxes can't drift visually; only the media
          // element differs (image ? <img> : embed <iframe>).
          <figure className="m-0 flex flex-col items-center gap-3">
            {image && (
              <>
                {/* Plain <img>, not next/image: kraidle asset src values are arbitrary
                    (sync-manifest URLs) and not declared under `images` in next.config,
                    so next/image rejects them. The enlarged image is the same asset the
                    inline view already loaded, so there's nothing to optimise here.
                    Radius matches the inline media (--kr-radius-hero-media). */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={shownSrc}
                  alt={image.alt}
                  className="max-h-[82vh] max-w-[88vw] rounded-(--kr-radius-hero-media) object-contain"
                  data-testid="platform-lightbox-image"
                />
              </>
            )}
            {embed && (
              // HTML-embed asset re-rendered enlarged. It's a fixed-layout
              // (1280×720, 16:9) doc, so — like the inline embeds — it renders at
              // native size in an aspect-locked box and is scaled to fit (seeded
              // pre-paint by setModalEmbedBox, kept in sync by the resize effect;
              // an iframe can't reflow). The box is capped to the same 88vw/82vh
              // envelope as the image path so any viewport fits; width picks
              // whichever of the two limits binds first.
              <div
                ref={setModalEmbedBox}
                className="relative aspect-[16/9] overflow-hidden rounded-(--kr-radius-hero-media)"
                style={{ width: 'min(88vw, calc(82vh * 16 / 9))' }}
                data-testid="platform-lightbox-embed"
              >
                <iframe
                  src={embed.src}
                  title={embed.alt}
                  loading="lazy"
                  // Same tightest-sandbox self-contained doc as the inline embed.
                  sandbox=""
                  aria-hidden={embed.alt ? 'false' : 'true'}
                  className="absolute top-0 left-0 origin-top-left border-0"
                  style={{ width: EMBED_NATIVE_WIDTH, height: EMBED_NATIVE_HEIGHT }}
                />
              </div>
            )}
            {(image?.alt || embed?.alt) && (
              <figcaption className="max-w-[88vw] text-center text-sm text-white/85">
                {image?.alt || embed?.alt}
              </figcaption>
            )}
          </figure>
        )}
      </Modal>
    </>
  );
}
